import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Flame, Shield, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { getAuthToken } from "@/lib/supabase";

export default function OAuthConsentPage() {
  const [, setLocation] = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [shouldRedirectToAuth, setShouldRedirectToAuth] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const scope = searchParams.get('scope') || 'read';
  const state = searchParams.get('state');
  const responseType = searchParams.get('response_type') || 'code';

  const scopes = scope.split(' ').filter(Boolean);

  const scopeDescriptions: Record<string, string> = {
    'read': 'View your profile information',
    'read:workouts': 'View your workout history',
    'write:workouts': 'Log workouts on your behalf',
    'read:progress': 'View your fitness progress',
    'read:programs': 'View your workout programs',
  };

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setShouldRedirectToAuth(true);
    }
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (shouldRedirectToAuth) {
      const returnUrl = encodeURIComponent(window.location.href);
      setLocation(`/auth?returnTo=${returnUrl}`);
    }
  }, [shouldRedirectToAuth, setLocation]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated || shouldRedirectToAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary">Redirecting to login...</div>
      </div>
    );
  }

  const handleAllow = async () => {
    setIsProcessing(true);
    try {
      const token = await getAuthToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/oauth/authorize', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          client_id: clientId,
          redirect_uri: redirectUri,
          scope,
          state,
          approved: true,
        }),
      });

      const data = await response.json();
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else if (data.error) {
        console.error('OAuth authorization error:', data.error_description);
      }
    } catch (error) {
      console.error('OAuth authorization error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeny = () => {
    if (redirectUri && state) {
      const url = new URL(redirectUri);
      url.searchParams.set('error', 'access_denied');
      url.searchParams.set('error_description', 'User denied the authorization request');
      if (state) url.searchParams.set('state', state);
      window.location.href = url.toString();
    } else {
      setLocation('/');
    }
  };

  if (!clientId || !redirectUri) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto p-3 bg-destructive/20 rounded-xl w-fit mb-4">
              <X className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle>Invalid Request</CardTitle>
            <CardDescription>
              Missing required OAuth parameters. Please try again from the application.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Button variant="outline" onClick={() => setLocation('/')}>
              Return to Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-primary/20 rounded-xl">
              <Flame className="w-8 h-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">
            myFit<span className="text-primary">Beats</span>
          </CardTitle>
          <CardDescription className="mt-4">
            An application is requesting access to your account
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Signed in as</p>
            <p className="font-medium">{user?.email}</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Shield className="w-4 h-4 text-primary" />
              <span>This application will be able to:</span>
            </div>
            <ul className="space-y-2">
              {scopes.map((s) => (
                <li key={s} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{scopeDescriptions[s] || s}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            By clicking Allow, you authorize this application to access your data according to its terms of service and privacy policy.
          </p>
        </CardContent>

        <CardFooter className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleDeny}
            disabled={isProcessing}
            data-testid="button-deny-oauth"
          >
            Deny
          </Button>
          <Button
            className="flex-1"
            onClick={handleAllow}
            disabled={isProcessing}
            data-testid="button-allow-oauth"
          >
            {isProcessing ? "Processing..." : "Allow"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
